import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Users, Mail, MessageSquare, LogOut,
  UsersRound, CalendarCog, Heart, Wallet, Menu, X, BookOpen, FileText, MapPin, Church, GraduationCap, Megaphone, Rss, ShieldCheck, Sparkles,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { LOGO_URL } from "../assets/images";

const NAV_LINKS = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Resumo" },
  { to: "/admin/comunidades", icon: MapPin, label: "Comunidades" },
  { to: "/admin/santuarios", icon: Church, label: "Santuários" },
  { to: "/admin/eventos", icon: CalendarCog, label: "Eventos" },
  { to: "/admin/inscricoes", icon: Users, label: "Inscrições" },
  { to: "/admin/membros", icon: UsersRound, label: "Sócios" },
  { to: "/admin/cotas", icon: Wallet, label: "Cotas" },
  { to: "/admin/biblioteca", icon: BookOpen, label: "Biblioteca" },
  { to: "/admin/percursos", icon: GraduationCap, label: "Percursos" },
  { to: "/admin/convocatorias", icon: Megaphone, label: "Convocatórias" },
  { to: "/admin/comunicados", icon: Rss, label: "Comunicados" },
  { to: "/admin/donativos", icon: Heart, label: "Donativos" },
  { to: "/admin/declaracoes", icon: FileText, label: "Declarações" },
  { to: "/admin/newsletter", icon: Mail, label: "Newsletter" },
  { to: "/admin/novenas", icon: Sparkles, label: "Novenas" },
  { to: "/admin/contactos", icon: MessageSquare, label: "Contactos" },
  { to: "/admin/acessos", icon: ShieldCheck, label: "Acessos" },
];

function Sidebar({ user, logout, onLinkClick, onClose }) {
  return (
    <aside className="w-64 h-full bg-[hsl(var(--primary))] text-white flex flex-col">
      <div className="px-6 py-7 border-b border-white/10 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="admin-home-link">
          <img src={LOGO_URL} alt="CIEES" className="h-10 w-10" />
          <div>
            <div className="display-heading text-lg">CIEES</div>
            <div className="text-[9px] tracking-[0.28em] uppercase text-white/60">
              Administração
            </div>
          </div>
        </a>
        {/* close button — only visible inside mobile drawer */}
        <button
          onClick={onClose}
          className="lg:hidden text-white/70 hover:text-white -mr-2 p-2"
          aria-label="Fechar menu"
          data-testid="admin-sidebar-close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
        {NAV_LINKS.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={onLinkClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                isActive
                  ? "bg-white/10 text-[hsl(var(--accent))]"
                  : "text-white/80 hover:text-white hover:bg-white/5"
              }`
            }
            data-testid={`admin-nav-${l.to.replace(/\//g, "-")}`}
          >
            <l.icon className="h-4 w-4" />
            {l.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-white/10 px-4 py-5">
        <div className="flex items-center gap-3">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="h-9 w-9 rounded-full border border-white/20"
            />
          ) : (
            <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center text-xs font-medium">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate">{user?.name}</div>
            <div className="text-[10px] text-white/60 truncate">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout-button"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/70 border border-white/15 px-3 py-2 hover:bg-white/10 hover:text-white transition-colors"
        >
          <LogOut className="h-3.5 w-3.5" /> Sair
        </button>
      </div>
    </aside>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false); // mobile drawer
  const closeDrawer = () => setOpen(false);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="admin-layout">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-[hsl(var(--primary))] text-white border-b border-white/10">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center justify-center h-10 w-10 -ml-2 text-white"
            aria-label="Abrir menu"
            data-testid="admin-sidebar-open"
          >
            <Menu className="h-5 w-5" />
          </button>
          <a href="/admin" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="CIEES" className="h-7 w-7" />
            <span className="display-heading text-base">CIEES</span>
            <span className="text-[9px] tracking-[0.22em] uppercase text-white/60 ml-1">Admin</span>
          </a>
          <button
            onClick={logout}
            data-testid="admin-logout-mobile"
            className="inline-flex items-center justify-center h-10 w-10 -mr-2 text-white/80 hover:text-white"
            aria-label="Sair"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar — fixed lateral */}
        <div className="hidden lg:flex lg:flex-col lg:w-64 lg:min-h-screen lg:sticky lg:top-0">
          <Sidebar user={user} logout={logout} onClose={closeDrawer} />
        </div>

        {/* Mobile drawer + backdrop */}
        {open && (
          <>
            <div
              onClick={closeDrawer}
              className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]"
              data-testid="admin-sidebar-backdrop"
            />
            <div
              className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 shadow-2xl"
              role="dialog"
              aria-modal="true"
              data-testid="admin-sidebar-drawer"
            >
              <Sidebar user={user} logout={logout} onClose={closeDrawer} onLinkClick={closeDrawer} />
            </div>
          </>
        )}

        {/* Main */}
        <main className="flex-1 min-h-screen overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
